import { Helmet } from 'react-helmet-async';

const SITE_NAME = "L'ETÉ";
const SITE_URL = 'https://ecommerce.moreira.uy';
const DEFAULT_IMAGE = '/og-image.jpg';

/**
 * Componente SEO reutilizable con meta tags y Schema.org
 */
const SEO = ({
  title,
  description,
  image,
  url,
  type = 'website',
  product = null,
  breadcrumbs = null,
  noindex = false,
  siteName
}) => {
  const resolvedSiteName = siteName || SITE_NAME;
  const fullTitle = title ? `${resolvedSiteName} | ${title}` : resolvedSiteName;
  const fullUrl = url ? `${SITE_URL}${url}` : SITE_URL;
  const fullImage = image ? (image.startsWith('http') ? image : `${SITE_URL}${image}`) : `${SITE_URL}${DEFAULT_IMAGE}`;

  // Schema.org Organization (siempre presente)
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    sameAs: [
      'https://www.instagram.com/',
      'https://www.facebook.com/'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+598-99-999-999',
      contactType: 'customer service',
      areaServed: 'UY',
      availableLanguage: 'Spanish'
    }
  };

  // Schema.org WebSite con SearchAction
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/tienda?search={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };

  // Schema.org Product (si se proporciona)
  const productSchema = product ? {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.images?.map(img => img.startsWith('http') ? img : `${SITE_URL}${img}`) || [fullImage],
    sku: product.sku || product._id,
    brand: {
      '@type': 'Brand',
      name: product.brand || SITE_NAME
    },
    offers: {
      '@type': 'Offer',
      url: fullUrl,
      priceCurrency: 'UYU',
      price: product.price,
      priceValidUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      availability: product.stock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      seller: {
        '@type': 'Organization',
        name: SITE_NAME
      }
    },
    ...(product.category && {
      category: product.category.name || product.category
    }),
    ...(product.rating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: product.rating,
        reviewCount: product.reviewCount || 1
      }
    })
  } : null;

  // Schema.org BreadcrumbList (si se proporciona)
  const breadcrumbSchema = breadcrumbs ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: `${SITE_URL}${crumb.url}`
    }))
  } : null;

  return (
    <Helmet>
      {/* Meta tags básicos */}
      <html lang="es" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content={resolvedSiteName} />
      <meta property="og:locale" content="es_UY" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      {productSchema && (
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      )}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
